import type { ComponentPropsWithoutRef, ForwardedRef } from "react";

import { inputStyles } from "@/shared/ui/form/input";
import { forwardRef, tw } from "@/shared/utils";
import { IconWrapper } from "../common";
import type { BaseFieldProps } from "./form.types";
import { Label } from "./label";
import { Message } from "./message";

type TextAreaProps = Omit<ComponentPropsWithoutRef<"textarea">, "id"> &
  BaseFieldProps;

export const TextArea = forwardRef(
  (
    {
      className,
      compact = true,
      containerClassName,
      disabled,
      error,
      id,
      label,
      left,
      message,
      right,
      readOnly,
      ...rest
    }: TextAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => (
    <div
      className={tw(
        "flex grow flex-col gap-1.5 text-neutral-900",
        containerClassName,
      )}
    >
      {!!label && <Label htmlFor={id}>{label}</Label>}

      <div
        className={tw(
          "relative items-center rounded-lg",
          !!disabled && "opacity-30",
        )}
      >
        {!!left && (
          <div
            className={tw(
              "pointer-events-none absolute top-1/2 -translate-y-1/2 pl-4",
              readOnly && "pl-0",
            )}
          >
            <IconWrapper
              size="sm"
              className={tw("fill-neutral-600", !!error && "fill-red-300")}
            >
              {left}
            </IconWrapper>
          </div>
        )}

        <textarea
          ref={ref}
          id={id}
          disabled={disabled}
          readOnly={readOnly}
          {...rest}
          className={tw(
            inputStyles().root(),
            !!left && "pl-10 read-only:pl-6",
            !!right && "pr-10",

            !!error && "border-red-700 focus:ring-red-100",
            className,
          )}
        />

        {!!right && (
          <div className="absolute top-1/2 right-0 -translate-y-1/2 pr-4">
            <IconWrapper size="sm" className="fill-secondary-beige-07">
              {right}
            </IconWrapper>
          </div>
        )}
      </div>

      {(!compact || !!message || !!error) && (
        <Message message={message} error={error} />
      )}
    </div>
  ),
);
